import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Plus, Trash2, X } from "lucide-react";
import { Button, Dialog, DialogPanel } from "@tremor/react";
import { toast } from "sonner";
import { createAlertRule } from "../../api/alerts.js";
import { fmtInterval } from "../../utils/format.js";

function AddRuleInlineForm({ monitorId, onCreated }) {
  const { t } = useTranslation("alert_rules");
  const [type, setType] = useState("consecutive_failures");
  const [count, setCount] = useState(3);
  const [thresholdMs, setThresholdMs] = useState(2000);
  const [reAlertMinutes, setReAlertMinutes] = useState("");
  const [saving, setSaving] = useState(false);

  const RULE_TYPE_OPTIONS = [
    { value: "consecutive_failures", label: t("types.consecutive_failures") },
    { value: "response_time", label: t("types.response_time") },
  ];

  const RE_ALERT_OPTIONS = [
    { value: "", label: t("re_alert.once") },
    { value: "5", label: t("re_alert.minutes", { count: 5 }) },
    { value: "15", label: t("re_alert.minutes", { count: 15 }) },
    { value: "30", label: t("re_alert.minutes", { count: 30 }) },
    { value: "60", label: t("re_alert.hour") },
    { value: "120", label: t("re_alert.hours", { count: 2 }) },
    { value: "360", label: t("re_alert.hours", { count: 6 }) },
    { value: "1440", label: t("re_alert.hours", { count: 24 }) },
  ];

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true);
    const config =
      type === "consecutive_failures"
        ? { count: Number(count) }
        : { threshold_ms: Number(thresholdMs) };
    const payload = {
      type,
      config,
      re_alert_minutes: reAlertMinutes ? Number(reAlertMinutes) : null,
    };
    try {
      const rule = await createAlertRule(monitorId, payload);
      toast.success(t("form.success_add"));
      onCreated(rule);
      setReAlertMinutes("");
    } catch {
      toast.error(t("form.error_add"));
    } finally {
      setSaving(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-gray-800">{t("form.title")}</h3>
          <p className="mt-1 text-xs text-gray-500">
            {t("form.subtitle")}
          </p>
        </div>
        <Button type="submit" disabled={saving} icon={Plus}>
          {saving ? t("form.saving") : t("form.add_button")}
        </Button>
      </div>

      <div>
        <label className="mb-1 block text-xs font-medium text-gray-600">{t("form.rule_type")}</label>
        <select
          value={type}
          onChange={(e) => setType(e.target.value)}
          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-teal-600"
        >
          {RULE_TYPE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>

      {type === "consecutive_failures" && (
        <div className="rounded-lg border border-gray-200 bg-white p-4 space-y-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
            {t("form.trigger_when")}
          </p>
          <div className="flex items-center gap-3">
            <input
              type="number"
              min={1}
              max={20}
              value={count}
              onChange={(e) => setCount(e.target.value)}
              className="w-20 rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-teal-600"
            />
            <span className="text-sm text-gray-600">{t("form.consecutive_failures")}</span>
          </div>
        </div>
      )}

      {type === "response_time" && (
        <div className="rounded-lg border border-gray-200 bg-white p-4 space-y-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
            {t("form.trigger_when")}
          </p>
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-600">{t("form.response_time_exceeds")}</span>
            <input
              type="number"
              min={1}
              value={thresholdMs}
              onChange={(e) => setThresholdMs(e.target.value)}
              className="w-24 rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-teal-600"
            />
            <span className="text-sm text-gray-600">ms</span>
          </div>
        </div>
      )}

      <div>
        <label className="mb-1 block text-xs font-medium text-gray-600">{t("form.re_alert")}</label>
        <select
          value={reAlertMinutes}
          onChange={(e) => setReAlertMinutes(e.target.value)}
          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-teal-600"
        >
          {RE_ALERT_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>
    </form>
  );
}

function AlertRulesDialog({ monitorId, rules, onClose, onCreated, onDelete }) {
  const { t } = useTranslation("alert_rules");

  return (
    <Dialog open onClose={onClose} static>
      <DialogPanel className="fixed inset-y-0 right-0 m-0 flex h-full w-full max-w-lg flex-col rounded-none bg-white p-0 shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
          <div>
            <h2 className="text-base font-semibold text-gray-900">{t("dialog.title")}</h2>
            <p className="mt-1 text-sm text-gray-500">
              {t("dialog.subtitle")}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-gray-400 hover:bg-gray-100"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5">
          <div className="mb-5 rounded-lg border border-gray-200 bg-gray-50 p-4">
            <AddRuleInlineForm monitorId={monitorId} onCreated={onCreated} />
          </div>

          {rules.length === 0 ? (
            <p className="rounded-lg border border-dashed border-gray-200 px-5 py-8 text-center text-sm text-gray-400">
              {t("dialog.empty")}
            </p>
          ) : (
            <ul className="divide-y divide-gray-100 rounded-lg border border-gray-200 bg-white">
              {rules.map((rule) => (
                <li
                  key={rule.id}
                  className="flex items-center justify-between gap-4 px-5 py-4"
                >
                  <div>
                    <p className="text-sm font-medium text-gray-800">
                      {rule.type === "consecutive_failures"
                        ? t("rule_description.consecutive_failures", { count: rule.config.count })
                        : t("rule_description.response_time", { threshold: rule.config.threshold_ms })}
                    </p>
                    <p className="mt-0.5 text-xs text-gray-400">
                      {rule.re_alert_minutes
                        ? t("rule_description.re_alert_every", { interval: fmtInterval(rule.re_alert_minutes * 60) })
                        : t("rule_description.alert_once")}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => onDelete(rule.id)}
                    className="rounded p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600"
                    title={t("dialog.delete_title")}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </DialogPanel>
    </Dialog>
  );
}

export { AlertRulesDialog, AddRuleInlineForm };
